import * as stylex from '@stylexjs/stylex';
import { useInfiniteQuery, useMutation, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useNumberFormatter } from 'react-aria';

import { ArticleListItem } from '@/components/article-list-item.component';
import { ArticleList } from '@/components/article-list.component';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger.hook';
import {
  EmptyState,
  Heading,
  Paragraph,
  ProgressIndicator,
  Text
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { articleMutations, articleQueries } from '@/resources/article/article.api';

export const Route = createFileRoute('/_app/_list/history')({
  component: RouteComponent,
  staticData: {
    contextBarComponent: ContextBarComponent,
  },
  loader: ({ context }) => context.queryClient.ensureInfiniteQueryData(articleQueries.seen),
  head: () => ({
    meta: [
      {
        title: 'TrendMD | History',
      },
    ],
  })
});

function RouteComponent() {
  const saveArticleMutation = useMutation(articleMutations.save());
  const unsaveArticleMutation = useMutation(articleMutations.unsave());
  const excludeArticleMutation = useMutation(articleMutations.exclude());
  const undoExcludeArticleMutation = useMutation(articleMutations.removeExclude());

  const {
    data,
    fetchNextPage,
    isFetching,
    isLoading,
    hasNextPage
  } = useSuspenseInfiniteQuery(articleQueries.seen);

  useInfiniteScrollTrigger({ isFetching, hasNextPage, fetchNextPage });

  const items = data?.pages?.flatMap((page) => page.articles) ?? [];

  return items.length > 0 ?
    <>
      <ArticleList aria-label="Seen articles list" items={items}>
        {({ key, index, style, ref, ...item }) => (
          <ArticleListItem
            data-index={index}
            id={item.id}
            isExcluded={item.excluded}
            isLast={index === items.length - 1}
            isSaved={item.saved}
            isSeen
            key={key}
            publicationDate={item.publication_date}
            publicationId={item.publication_id}
            publicationName={item.publication_name}
            ref={ref}
            style={style}
            title={item.title}
            url={item.url}
            onExcludePress={() => excludeArticleMutation.mutate({ article_id: item.id })}
            onSavePress={() => saveArticleMutation.mutate({ article_id: item.id })}
            onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: item.id })}
            onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: item.id })}
          />)}
      </ArticleList>
      {!isLoading && hasNextPage && <ProgressIndicator size={24} style={progressIndicatorStyles.base} />}
    </> :
    <EmptyState style={emptyStateStyles.base}>
      <Heading color="placeholder" size={24}>Your view history is empty</Heading>
      <Paragraph color="placeholder" size={14}>Articles from partner publishers will appear here as you read them</Paragraph>
    </EmptyState>;
}

function ContextBarComponent() {
  const numberFormatter = useNumberFormatter();

  const seenArticlesQuery = useInfiniteQuery(articleQueries.seen);

  const total = seenArticlesQuery.data?.pages[0]?.total ? numberFormatter.format(seenArticlesQuery.data?.pages[0]?.total) : '';

  return (
    <div {...stylex.props(contextBarStyles.base)}>
      <Heading level={1} size={24}>History</Heading>
      <Text color="placeholder" size={14}>{total}</Text>
    </div>
  );
}

const contextBarStyles = stylex.create({
  base: {
    alignItems: 'baseline',
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    marginLeft: {
      default: 0,
      [breakpoints.large]: 'auto',
    },
    marginRight: {
      default: 0,
      [breakpoints.large]: 'auto',
    },
    paddingBlock: 12,
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
    width: {
      default: '100%',
      [breakpoints.large]: 728,
    },
  },
});

const progressIndicatorStyles = stylex.create({
  base: {
    marginBottom: 20,
    marginTop: 56,
  }
});

const emptyStateStyles = stylex.create({
  base: {
    paddingTop: 8,
  },
});
